import React, { useState, useEffect } from 'react';
import {
  Modal,
  Typography,
  TextField,
  Box,
  Button,
  IconButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {
  getFirestore,
  doc,
  collection,
  query,
  where,
  getDocs,
  writeBatch,
  deleteDoc,
  updateDoc,
  getDoc,
} from 'firebase/firestore';
import CustomerSelectionTable from './CustomerSelectionTable';
import ConflictResolutionModal from './ConflictResolutionModal';
import ConfirmUpdateModal from './ConfirmUpdateModal';
import { auth } from './firebaseConfig';

/**
 * Çalışma süresini dakikaya çeviren fonksiyon - iOS HopsPlus ile AYNI MANTIK
 */
const parseWorkingTime = (timeString) => {
  if (!timeString || timeString.toString().trim() === '') return 0;
  const input = timeString.toString().trim();
  if (input.includes(':') && !input.toLowerCase().includes('saat')) {
    const parts = input.split(':');
    if (parts.length === 2) {
      return (parseInt(parts[0].trim(), 10) || 0) * 60 + (parseInt(parts[1].trim(), 10) || 0);
    }
  }
  if (input.toLowerCase().includes('saat')) {
    let hours = 0, minutes = 0;
    const saatMatch = input.toLowerCase().match(/(\d+)\s*saat/);
    if (saatMatch) hours = parseInt(saatMatch[1], 10) || 0;
    const dakikaMatch = input.toLowerCase().match(/(\d+)\s*(dakika|dk)/);
    if (dakikaMatch) minutes = parseInt(dakikaMatch[1], 10) || 0;
    return hours * 60 + minutes;
  }
  const normalized = input.replace(',', '.');
  const decimalHours = parseFloat(normalized);
  if (!isNaN(decimalHours) && normalized.includes('.')) return Math.round(decimalHours * 60);
  const justNumber = parseInt(input, 10);
  if (!isNaN(justNumber)) return justNumber;
  return 0;
};

const ModalBox = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 700,
  backgroundColor: '#ffffff',
  color: '#000000',
  boxShadow: theme.shadows[5],
  padding: theme.spacing(4),
  maxHeight: '90vh',
  overflowY: 'auto',
  border: '1px solid #ccc',
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(0.5),
  marginLeft: theme.spacing(1),
  width: '30px',
  height: '30px',
}));

const normalizeString = (str) => {
  return str.trim().replace(/\s+/g, ' '); // Baş, son ve fazla boşlukları temizler
};

const EditSantiyeModal = ({
  isOpen,
  onClose,
  selectedSantiye,
  setSelectedSantiye,
  setCustomerShantiyeler,
  customerShantiyeler,
  setAlertOpen,
  setError,
  setSuccessMessage,
  approvedCustomers,
}) => {
  const [editSantiyeName, setEditSantiyeName] = useState('');
  const [editSantiyePhone, setEditSantiyePhone] = useState('');
  const [editSantiyeEmail, setEditSantiyeEmail] = useState('');
  const [editSantiyeCariCode, setEditSantiyeCariCode] = useState('');
  const [isCustomerSelectionOpen, setIsCustomerSelectionOpen] = useState(false);
  const [isConflictModalOpen, setIsConflictModalOpen] = useState(false);
  const [conflictingSantiye, setConflictingSantiye] = useState(null);
  const [updateChanges, setUpdateChanges] = useState([]);
  const [isConfirmUpdateOpen, setIsConfirmUpdateOpen] = useState(false);
  
  // Eski şantiye verilerini tutan state
  const [oldSantiyeData, setOldSantiyeData] = useState(null);
  
  // Çakışma kontrolü için state'ler
  const [nameConflict, setNameConflict] = useState(false);
  const [codeConflict, setCodeConflict] = useState(false);
  const [conflictingNameSantiye, setConflictingNameSantiye] = useState(null);
  const [conflictingCodeSantiye, setConflictingCodeSantiye] = useState(null);

  useEffect(() => {
    if (selectedSantiye) {
      // Modal ilk açıldığında orijinal verileri kaydet
      setOldSantiyeData({
        'Şantiye Adı': selectedSantiye['Şantiye Adı'] || selectedSantiye['Müşteri Adı'] || '',
        'Şantiye Cari Kodu': selectedSantiye['Şantiye Cari Kodu'] || selectedSantiye.cariCode || ''
      });

      setEditSantiyeName(selectedSantiye['Şantiye Adı'] || selectedSantiye['Müşteri Adı'] || '');
      setEditSantiyePhone(selectedSantiye['Telefon'] || '');
      setEditSantiyeEmail(selectedSantiye['E-posta'] || '');
      setEditSantiyeCariCode(selectedSantiye['Şantiye Cari Kodu'] || selectedSantiye.cariCode || '');
      
      console.log('Seçilen şantiye verileri:', {
        santiyeAdi: selectedSantiye['Şantiye Adı'],
        musteriAdi: selectedSantiye['Müşteri Adı'],
        telefon: selectedSantiye['Telefon'],
        email: selectedSantiye['E-posta'],
        cariKod: selectedSantiye['Şantiye Cari Kodu'] || selectedSantiye.cariCode
      });
    }
  }, [selectedSantiye]);

  // Şantiye adı değiştikçe çakışma kontrolü
  useEffect(() => {
    if (!editSantiyeName.trim()) {
      setNameConflict('empty');
      setConflictingNameSantiye(null);
    } else {
      const conflictSantiye = approvedCustomers.find(c => 
        c.id !== selectedSantiye?.id && 
        c['Şantiye Adı']?.trim().toLowerCase() === editSantiyeName.trim().toLowerCase()
      );
      setNameConflict(conflictSantiye ? 'conflict' : false);
      setConflictingNameSantiye(conflictSantiye || null);
    }
  }, [editSantiyeName, approvedCustomers, selectedSantiye?.id]);

  // Şantiye cari kodu değiştikçe çakışma kontrolü
  useEffect(() => {
    if (!editSantiyeCariCode.trim()) {
      setCodeConflict('empty');
      setConflictingCodeSantiye(null);
    } else {
      const conflictSantiye = approvedCustomers.find(c => 
        c.id !== selectedSantiye?.id && 
        (c['Şantiye Cari Kodu'] || '').trim().toLowerCase() === editSantiyeCariCode.trim().toLowerCase()
      );
      setCodeConflict(conflictSantiye ? 'conflict' : false);
      setConflictingCodeSantiye(conflictSantiye || null);
    }
  }, [editSantiyeCariCode, approvedCustomers, selectedSantiye?.id]);

  const handleUpdateSantiye = async () => {
    if (!editSantiyeName.trim() || !editSantiyeCariCode.trim()) {
      setError('Şantiye adı ve cari kodu gereklidir.');
      setAlertOpen(true);
      return;
    }

    await checkConflictsAndUpdate();
  };

  const checkConflictsAndUpdate = async () => {
    const db = getFirestore();
    const userEmail = auth.currentUser?.email;
    
    if (!userEmail) {
      setError('Kullanıcı oturumu bulunamadı');
      setAlertOpen(true);
      return;
    }

    try {
      console.log('🔍 Şantiye çakışma kontrolü başlıyor:', {
        editSantiyeName: editSantiyeName.trim(),
        editSantiyeCariCode: editSantiyeCariCode.trim(),
        originalName: oldSantiyeData?.['Şantiye Adı'],
        originalCariCode: oldSantiyeData?.['Şantiye Cari Kodu'],
        selectedSantiyeId: selectedSantiye.id
      });

      const customerListRef = collection(db, `users/${userEmail}/customerList`);
      
      // Şantiye adı çakışması kontrolü
      const nameConflictQuery = query(
        customerListRef,
        where('Şantiye Adı', '==', editSantiyeName.trim()),
        where('__name__', '!=', selectedSantiye.id) // Kendi kendisiyle çakışma olmasın
      );
      
      // Cari kod çakışması kontrolü
      const cariCodeConflictQuery = query(
        customerListRef,
        where('Şantiye Cari Kodu', '==', editSantiyeCariCode.trim()),
        where('__name__', '!=', selectedSantiye.id) // Kendi kendisiyle çakışma olmasın
      );

      const [nameConflictSnapshot, cariCodeConflictSnapshot] = await Promise.all([
        getDocs(nameConflictQuery),
        getDocs(cariCodeConflictQuery),
      ]);

      // Eğer çakışma varsa modal aç
      if (!nameConflictSnapshot.empty || !cariCodeConflictSnapshot.empty) {
        const conflictingDoc = !nameConflictSnapshot.empty
          ? nameConflictSnapshot.docs[0]
          : cariCodeConflictSnapshot.docs[0];

        console.log('🚨 Şantiye çakışması bulundu, çakışma çözümleme modalı açılıyor');
        setConflictingSantiye({ ...conflictingDoc.data(), id: conflictingDoc.id });
        setIsConflictModalOpen(true);
        return;
      }

      console.log('✅ Şantiye çakışması tespit edilmedi, güncelleme devam ediyor');
      proceedWithUpdate();
    } catch (error) {
      console.error('Şantiye çakışma kontrolü sırasında hata:', error);
      setError(`Şantiye çakışma kontrolü sırasında bir hata oluştu: ${error.message}`);
      setAlertOpen(true);
    }
  };

  const proceedWithDirectUpdate = async () => {
    const db = getFirestore();
    const userEmail = auth.currentUser?.email;
    
    if (!userEmail) {
      setError('Kullanıcı oturumu bulunamadı');
      setAlertOpen(true);
      return;
    }

    try {
      const batch = writeBatch(db);

      // Şantiyeyi güncelle
      const santiyeRef = doc(db, `users/${userEmail}/customerList`, selectedSantiye.id);
      const updateData = {
        'Şantiye Adı': editSantiyeName.trim(),
        'Müşteri Adı': editSantiyeName.trim(), // Müşteri adını da güncelle
        'Telefon': editSantiyePhone.trim(),
        'E-posta': editSantiyeEmail.trim(),
        'Şantiye Cari Kodu': editSantiyeCariCode.trim(),
        'cariCode': editSantiyeCariCode.trim(), // Normal cariCode'u da güncelle
        'Onay': 'Onaylandı',
        'updatedAt': new Date().toISOString(),
        'updatedBy': userEmail
      };
      batch.update(santiyeRef, updateData);

      // İlgili puantajları güncelle - timeSheets koleksiyonunu kullan
      const timeSheetsRef = collection(db, `users/${userEmail}/timeSheets`);
      const puantajQuery = query(
        timeSheetsRef,
        where('Müşteri Adı', '==', oldSantiyeData?.['Şantiye Adı'] || selectedSantiye['Şantiye Adı'] || selectedSantiye['Müşteri Adı'])
      );

      const puantajSnapshot = await getDocs(puantajQuery);
      
      // Tüm puantajları güncelle
      puantajSnapshot.forEach((puantajDoc) => {
        batch.update(puantajDoc.ref, {
          'Müşteri Adı': editSantiyeName.trim(),
          'Cari Kodu': editSantiyeCariCode.trim()
        });
      });

      // Tüm güncellemeleri tek seferde gerçekleştir
      await batch.commit();

      setSuccessMessage('Şantiye ve ilgili puantaj verileri başarıyla güncellendi.');
      setAlertOpen(true);
      onClose();
    } catch (error) {
      console.error('Şantiye güncellenirken hata:', error);
      setError('Şantiye güncellenirken bir hata oluştu.');
      setAlertOpen(true);
    }
  };

  const proceedWithUpdate = async () => {
    const db = getFirestore();
    const userEmail = auth.currentUser?.email;
    
    if (!userEmail) {
      setError('Kullanıcı oturumu bulunamadı');
      setAlertOpen(true);
      return;
    }

    try {
      // User-based path ile güncelleme
      const santiyeRef = doc(db, `users/${userEmail}/customerList`, selectedSantiye.id);
      await updateDoc(santiyeRef, {
        'Müşteri Adı': editSantiyeName.trim(),
        'Şantiye Adı': editSantiyeName.trim(),
        Telefon: editSantiyePhone.trim(),
        'E-posta': editSantiyeEmail.trim(),
        'Şantiye Cari Kodu': editSantiyeCariCode.trim(),
        cariCode: editSantiyeCariCode.trim(),
        Onay: 'Onaylandı',
        updatedAt: new Date().toISOString(),
        updatedBy: userEmail
      });

      setSuccessMessage('Şantiye başarıyla güncellendi.');
      setAlertOpen(true);
      onClose();
    } catch (error) {
      console.error('Şantiye güncellenirken hata oluştu:', error);
      setError('Şantiye güncellenirken bir hata oluştu.');
      setAlertOpen(true);
    }
  };

  const handleConflictResolution = async () => {
    const db = getFirestore();
    const userEmail = auth.currentUser?.email;
    
    if (!userEmail) {
      setError('Kullanıcı oturumu bulunamadı');
      setAlertOpen(true);
      return;
    }

    try {
      if (!conflictingSantiye || !conflictingSantiye.id) {
        throw new Error('Çakışan şantiye bilgisi eksik veya geçersiz.');
      }

      console.log('🔄 Şantiye birleştirme işlemi başlıyor:', {
        çakışanŞantiye: conflictingSantiye['Şantiye Adı'],
        güncellenecekŞantiye: editSantiyeName.trim(),
        çakışanId: conflictingSantiye.id,
        güncellenecekId: selectedSantiye.id
      });

      // Batch işlemi oluştur
      const batch = writeBatch(db);

      // 1. Çakışan şantiyenin puantajlarını yeni şantiye adıyla güncelle
      const timeSheetsRef = collection(db, `users/${userEmail}/timeSheets`);
      const conflictingPuantajQuery = query(
        timeSheetsRef,
        where('Müşteri Adı', '==', conflictingSantiye['Şantiye Adı'])
      );
      
      const conflictingPuantajSnapshot = await getDocs(conflictingPuantajQuery);
      console.log(`📊 Çakışan şantiyenin puantaj sayısı: ${conflictingPuantajSnapshot.size}`);
      
      // Makine saatlik ücret bilgisini alma fonksiyonu
      const getMachineHourlyRate = async (machineName) => {
        try {
          const machinesRef = collection(db, `users/${userEmail}/machines`);
          const machineQuery = query(machinesRef, where('MakineAdı', '==', machineName));
          const machineSnapshot = await getDocs(machineQuery);
          
          if (!machineSnapshot.empty) {
            const machineData = machineSnapshot.docs[0].data();
            const hourlyRate = parseFloat(machineData.BirimFiyat || machineData['Birim Fiyat'] || '0');
            console.log(`🔧 Makine saatlik ücreti bulundu: ${machineName} = ${hourlyRate} TL/saat`);
            return hourlyRate;
          } else {
            console.warn(`⚠️ Makine bulunamadı: ${machineName}`);
            return 0;
          }
        } catch (error) {
          console.error('❌ Makine saatlik ücreti alınırken hata:', error);
          return 0;
        }
      };

      // İyileştirilmiş puantaj tutarı hesaplama fonksiyonu
      const calculatePuantajAmount = async (puantajData) => {
        console.log('💰 Şantiye düzenleme puantaj tutar hesaplaması başlıyor:', puantajData);
        
        // Çalışma saati bilgisini farklı field adlarından al
        let workingTime = puantajData['Toplam Çalışma Süresi'] || 
                         puantajData.totalWorkingTime || 
                         puantajData['Total Working Time'] ||
                         puantajData['toplamCalisSuresi'] ||
                         puantajData['toplam_calista_suresi'] ||
                         '0:00';
        
        // Eğer toplam çalışma süresi yoksa, saat bilgilerinden hesapla
        if (!workingTime || workingTime === '0:00' || workingTime === '00:00') {
          console.log('📊 Toplam çalışma süresi bulunamadı, saat bilgilerinden hesaplanıyor...');
          
          let totalMinutes = 0;
          
          // 1. Vardiya
          const start1 = puantajData['1. Başlangıç Saati'] || puantajData['startTime1'] || puantajData['start1'];
          const end1 = puantajData['1. Bitiş Saati'] || puantajData['endTime1'] || puantajData['end1'];
          
          if (start1 && end1) {
            const duration1 = calculateTimeDuration(start1, end1);
            totalMinutes += duration1;
            console.log(`⏰ 1. Vardiya: ${start1} - ${end1} = ${duration1} dakika`);
          }
          
          // 2. Vardiya
          const start2 = puantajData['2. Başlangıç Saati'] || puantajData['startTime2'] || puantajData['start2'];
          const end2 = puantajData['2. Bitiş Saati'] || puantajData['endTime2'] || puantajData['end2'];
          
          if (start2 && end2) {
            const duration2 = calculateTimeDuration(start2, end2);
            totalMinutes += duration2;
            console.log(`⏰ 2. Vardiya: ${start2} - ${end2} = ${duration2} dakika`);
          }
          
          // 3. Vardiya (eğer varsa)
          const start3 = puantajData['3. Başlangıç Saati'] || puantajData['startTime3'] || puantajData['start3'];
          const end3 = puantajData['3. Bitiş Saati'] || puantajData['endTime3'] || puantajData['end3'];
          
          if (start3 && end3) {
            const duration3 = calculateTimeDuration(start3, end3);
            totalMinutes += duration3;
            console.log(`⏰ 3. Vardiya: ${start3} - ${end3} = ${duration3} dakika`);
          }
          
          // Hesaplanan toplam süreyi HH:MM formatına çevir
          const hours = Math.floor(totalMinutes / 60);
          const minutes = totalMinutes % 60;
          workingTime = `${hours}:${minutes.toString().padStart(2, '0')}`;
          console.log(`📊 Hesaplanan toplam çalışma süresi: ${workingTime} (${totalMinutes} dakika)`);
        }
        
        // Saatlik ücret bilgisini farklı field adlarından al
        let hourlyRate = parseFloat(
          puantajData['Saatlik Ücret'] || 
          puantajData.hourlyRate || 
          puantajData['Hourly Rate'] ||
          puantajData['saatlikUcret'] ||
          puantajData['saatlik_ucret'] ||
          puantajData['BirimFiyat'] ||
          puantajData['birimFiyat'] ||
          puantajData['birim_fiyat'] ||
          '0'
        );
        
        // Eğer saatlik ücret bulunamazsa makine bilgilerinden çek
        if (hourlyRate === 0) {
          console.log('💰 Puantajda saatlik ücret bulunamadı, makine bilgilerinden çekiliyor...');
          const machineName = puantajData['Makine İsmi'] || puantajData['machineName'] || puantajData['Makine Adı'];
          if (machineName) {
            hourlyRate = await getMachineHourlyRate(machineName);
          }
        }
        
        console.log(`💰 Saatlik ücret: ${hourlyRate} TL`);
        
        // Çalışma saatini hesapla - iOS HopsPlus ile AYNI MANTIK
        const totalMinutes = parseWorkingTime(workingTime);
        const totalHours = totalMinutes / 60;
        
        const totalAmount = totalHours * hourlyRate;
        
        console.log(`📊 Şantiye düzenleme tutar hesaplama sonucu: ${totalHours} saat × ${hourlyRate} TL = ${totalAmount} TL`);
        
        return totalAmount;
      };
      
      // Saat aralığından dakika hesaplama yardımcı fonksiyonu
      const calculateTimeDuration = (startTime, endTime) => {
        if (!startTime || !endTime) return 0;
        
        const parseTime = (timeStr) => {
          const [hours, minutes] = timeStr.split(':').map(Number);
          if (isNaN(hours) || isNaN(minutes)) return null;
          return hours * 60 + minutes;
        };
        
        const startMinutes = parseTime(startTime);
        const endMinutes = parseTime(endTime);
        
        if (startMinutes === null || endMinutes === null) return 0;
        
        // Gece yarısı geçişi kontrolü
        let duration = endMinutes - startMinutes;
        if (duration < 0) {
          duration += 24 * 60; // Ertesi güne geçiş
        }
        
        return duration;
      };

      // Aktarılan puantajların detaylarını topla (cari geçmişi için)
      const transferredPuantajlar = [];
      
      // Çakışan şantiyenin tüm puantajlarını yeni şantiye adıyla güncelle
      for (const puantajDoc of conflictingPuantajSnapshot.docs) {
        const puantajData = puantajDoc.data();
        
        // Puantaj detaylarını kaydet (cari geçmişi için) - async olarak hesapla
        const totalAmount = await calculatePuantajAmount(puantajData) || 0;
        
        transferredPuantajlar.push({
          id: puantajDoc.id,
          puantajCount: puantajData['Puantaj No'] || puantajData.puantajCount || 'Bilinmeyen',
          machineName: puantajData['Makine İsmi'] || puantajData.machineName || 'Bilinmeyen',
          totalWorkingTime: puantajData['Toplam Çalışma Süresi'] || puantajData.totalWorkingTime || '0:00',
          date: puantajData['Tarih'] || puantajData.date || new Date().toISOString().split('T')[0],
          hourlyRate: puantajData['Saatlik Ücret'] || puantajData.hourlyRate || 0,
          totalAmount: totalAmount
        });
        
        batch.update(puantajDoc.ref, {
          'Müşteri Adı': editSantiyeName.trim(),
          'Cari Kodu': editSantiyeCariCode.trim(),
          'mergedFrom': conflictingSantiye['Şantiye Adı'], // Hangi şantiyeden birleştirildiği
          'mergedAt': new Date().toISOString(),
          'mergedBy': userEmail,
          'mergedReason': `🏗️ Şantiye birleştirme: "${conflictingSantiye['Şantiye Adı']}" → "${editSantiyeName.trim()}"`,
          'originalSantiyeName': conflictingSantiye['Şantiye Adı'] // Orijinal şantiye adını sakla
        });
      }

      // 2. Eski şantiye adına sahip puantajları da güncelle (eğer şantiye adı değişiyorsa)
      const oldSantiyeName = selectedSantiye['Şantiye Adı'] || selectedSantiye['Müşteri Adı'];
      if (oldSantiyeName !== editSantiyeName.trim()) {
        const oldPuantajQuery = query(
          timeSheetsRef,
          where('Müşteri Adı', '==', oldSantiyeName)
        );
        
        const oldPuantajSnapshot = await getDocs(oldPuantajQuery);
        console.log(`📊 Eski şantiye adının puantaj sayısı: ${oldPuantajSnapshot.size}`);
        
        oldPuantajSnapshot.forEach((puantajDoc) => {
          batch.update(puantajDoc.ref, {
            'Müşteri Adı': editSantiyeName.trim(),
            'Cari Kodu': editSantiyeCariCode.trim(),
            'updatedAt': new Date().toISOString(),
            'updatedBy': userEmail
          });
        });
      }

      // 3. CARİ KASA BORÇ BİRLEŞTİRME İŞLEMİ
      console.log('💰 Cari kasa borç birleştirme işlemi başlıyor...');
      
      // Çakışan şantiyenin borç bilgilerini al
      const conflictingDebtRef = doc(db, `users/${userEmail}/customerList/${conflictingSantiye.id}/debt/current`);
      const conflictingDebtDoc = await getDoc(conflictingDebtRef);
      
      // Ana şantiyenin borç bilgilerini al
      const mainDebtRef = doc(db, `users/${userEmail}/customerList/${selectedSantiye.id}/debt/current`);
      const mainDebtDoc = await getDoc(mainDebtRef);
      
      let conflictingDebtData = null;
      let mainDebtData = null;
      
      if (conflictingDebtDoc.exists()) {
        conflictingDebtData = conflictingDebtDoc.data();
      }
      
      if (mainDebtDoc.exists()) {
        mainDebtData = mainDebtDoc.data();
      }
      
      // Borç birleştirme hesaplamaları
      const conflictingAmount = conflictingDebtData?.amount || 0;
      const conflictingHistory = conflictingDebtData?.history || [];
      const mainAmount = mainDebtData?.amount || 0;
      const mainHistory = mainDebtData?.history || [];
      
      console.log(`💰 Çakışan şantiye borcu: ${conflictingAmount} TL (${conflictingHistory.length} işlem)`);
      console.log(`💰 Ana şantiye borcu: ${mainAmount} TL (${mainHistory.length} işlem)`);
      
      // Yeni toplam borç miktarı
      const newTotalAmount = mainAmount + conflictingAmount;
      
      // Çakışan şantiyenin history'sini transfer olarak işaretle
      const transferredHistory = conflictingHistory.map(historyItem => ({
        ...historyItem,
        transferredFrom: conflictingSantiye['Şantiye Adı'],
        transferredTo: editSantiyeName.trim(),
        transferredAt: new Date().toISOString(),
        transferredBy: userEmail
      }));
      
      // Transfer bilgi kaydı oluştur (eğer çakışan şantiyenin borcu varsa)
      let transferInfoEntry = null;
      if (conflictingAmount > 0 || conflictingHistory.length > 0) {
        transferInfoEntry = {
          id: `merge_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          amount: 0, // Sıfır - ekstra borç yaratmaz, sadece bilgi amaçlı
          date: new Date().toISOString(),
                     description: `🏗️ ŞANTİYE BİRLEŞTİRME: "${conflictingSantiye['Şantiye Adı']}" şantiyesinden "${editSantiyeName.trim()}" şantiyesine aktarım (Toplam: ${conflictingAmount} TL, İşlem: ${conflictingHistory.length} adet)`,
          type: 'transfer_in',
          transferFrom: conflictingSantiye['Şantiye Adı'],
          transferTo: editSantiyeName.trim(),
          isInfoOnly: true, // Sadece bilgi amaçlı işaret
          mergeOperation: true
        };
      }
      
      // Aktarılan puantajlar için cari geçmişi kayıtları oluştur
      const puantajHistoryEntries = transferredPuantajlar.map(puantaj => {
        // Tarihi formatla (DD.MM.YYYY -> ISO)
        let puantajDate = puantaj.date;
        if (puantaj.date && puantaj.date.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
          const [day, month, year] = puantaj.date.split('.');
          puantajDate = new Date(`${year}-${month}-${day}`).toISOString();
        } else if (puantaj.date && !puantaj.date.includes('T')) {
          puantajDate = new Date(puantaj.date + 'T12:00:00.000Z').toISOString();
        }
        
        // Puantaj işlemleri her zaman borç olmalı, ödeme değil
        return {
          id: `transferred_puantaj_${puantaj.id}_${Date.now()}`,
          amount: Math.abs(puantaj.totalAmount),
          date: puantajDate,
          description: `📊 PUANTAJ AKTARIM: ${puantaj.puantajCount} - ${puantaj.machineName} (${puantaj.totalWorkingTime}) | "${conflictingSantiye['Şantiye Adı']}" → "${editSantiyeName.trim()}"`,
          type: 'increase', // Puantaj işlemleri her zaman borç olmalı
          transferFrom: conflictingSantiye['Şantiye Adı'],
          transferTo: editSantiyeName.trim(),
          transferredAt: new Date().toISOString(),
          transferredBy: userEmail,
          // Puantaj detayları
          puantajCode: puantaj.puantajCount,
          machineName: puantaj.machineName,
          machineHours: puantaj.totalWorkingTime,
          hourlyRate: puantaj.hourlyRate,
          originalPuantajId: puantaj.id,
          transferType: 'puantaj_merge',
          mergeOperation: true
        };
      });
      
      // Ana şantiyenin yeni history'sini oluştur
      const newMainHistory = [
        ...mainHistory,
        ...(transferInfoEntry ? [transferInfoEntry] : []), // Transfer bilgi kaydı
        ...transferredHistory, // Çakışan şantiyenin gerçek işlemleri
        ...puantajHistoryEntries // Aktarılan puantajların detayları
      ];
      
      // Ana şantiyenin borç bilgilerini güncelle
      const mainDebtUpdateData = {
        amount: newTotalAmount,
        history: newMainHistory,
        lastUpdated: new Date().toISOString(),
        customerName: editSantiyeName.trim(),
        mergedFrom: conflictingSantiye['Şantiye Adı'],
        mergedAt: new Date().toISOString()
      };
      
      // Batch'e ana şantiyenin borç güncellemesini ekle
      batch.set(mainDebtRef, mainDebtUpdateData);
      
      // Çakışan şantiyenin borç bilgilerini temizle/sıfırla
      if (conflictingDebtDoc.exists()) {
        const conflictingDebtUpdateData = {
          amount: 0,
          history: [],
          lastUpdated: new Date().toISOString(),
          customerName: conflictingSantiye['Şantiye Adı'],
          transferredTo: editSantiyeName.trim(),
          transferredAt: new Date().toISOString(),
          transferredBy: userEmail
        };
        
        batch.set(conflictingDebtRef, conflictingDebtUpdateData);
      }
      
      console.log(`💰 Cari kasa birleştirme hazırlandı: ${conflictingAmount} TL → ${newTotalAmount} TL`);

      // 4. Çakışan şantiyeyi sil
      const conflictingSantiyeRef = doc(db, `users/${userEmail}/customerList`, conflictingSantiye.id);
      batch.delete(conflictingSantiyeRef);

      // 5. Ana şantiyeyi güncelle
      const santiyeRef = doc(db, `users/${userEmail}/customerList`, selectedSantiye.id);
      const updateData = {
        'Müşteri Adı': editSantiyeName.trim(),
        'Şantiye Adı': editSantiyeName.trim(),
        'Telefon': editSantiyePhone.trim(),
        'E-posta': editSantiyeEmail.trim(),
        'Şantiye Cari Kodu': editSantiyeCariCode.trim(),
        'cariCode': editSantiyeCariCode.trim(),
        'Onay': 'Onaylandı',
        'updatedAt': new Date().toISOString(),
        'updatedBy': userEmail,
        'mergedWith': conflictingSantiye['Şantiye Adı'] // Hangi şantiye ile birleştirildiği
      };
      batch.update(santiyeRef, updateData);

      // 6. Batch işlemini commit et
      await batch.commit();

      console.log('✅ Şantiye birleştirme işlemi tamamlandı:', {
        birleştirilenPuantajSayısı: conflictingPuantajSnapshot.size,
        güncellenecekŞantiye: editSantiyeName.trim(),
        çakışanŞantiyeSilindi: conflictingSantiye['Şantiye Adı'],
        birleştirilenBorç: conflictingAmount,
        yeniToplamBorç: newTotalAmount,
        birleştirilenİşlemSayısı: conflictingHistory.length
      });

      // Detaylı başarı mesajı oluştur
      let detailedMessage = `🎉 ŞANTİYE BİRLEŞTİRME TAMAMLANDI!\n\n`;
      
      // Şantiye bilgileri
      detailedMessage += `📋 SONUÇ:\n`;
      detailedMessage += `• "${conflictingSantiye['Şantiye Adı']}" şantiyesi silindi\n`;
      detailedMessage += `• "${selectedSantiye['Şantiye Adı'] || selectedSantiye['Müşteri Adı']}" şantiyesi güncellendi\n`;
      detailedMessage += `• Yeni adı: "${editSantiyeName.trim()}"\n`;
      detailedMessage += `• Yeni cari kodu: "${editSantiyeCariCode.trim()}"\n\n`;
      
      // Puantaj bilgileri
      const currentSantiyeName = selectedSantiye['Şantiye Adı'] || selectedSantiye['Müşteri Adı'];
      let totalPuantajCount = conflictingPuantajSnapshot.size;
      
            // Eski şantiye adından güncellenen puantajlar varsa say
      if (currentSantiyeName !== editSantiyeName.trim()) {
        // Bu durumda eski şantiye puantajları da güncellenmiş olacak
        const totalPuantajAmount = transferredPuantajlar.reduce((sum, p) => sum + p.totalAmount, 0);
        
        detailedMessage += `📊 PUANTAJ AKTARİMİ:\n`;
        detailedMessage += `• ${conflictingPuantajSnapshot.size} adet puantaj "${conflictingSantiye['Şantiye Adı']}" şantiyesinden aktarıldı\n`;
        detailedMessage += `• "${currentSantiyeName}" şantiyesinin tüm puantajları "${editSantiyeName.trim()}" olarak güncellendi\n`;
        detailedMessage += `• Toplam puantaj tutarı: ${totalPuantajAmount.toLocaleString('tr-TR')} TL\n`;
        detailedMessage += `• Puantajlarda "mergedFrom" etiketi ile kaynak şantiye gösterildi\n`;
        detailedMessage += `• Her puantaj cari kasa geçmişinde ayrı ayrı görünecek\n\n`;
        
        // Aktarılan puantajların listesi
        if (transferredPuantajlar.length > 0) {
          detailedMessage += `📋 AKTARILAN PUANTAJLAR:\n`;
          transferredPuantajlar.forEach((puantaj, index) => {
            detailedMessage += `  ${index + 1}. ${puantaj.puantajCount} - ${puantaj.machineName} (${puantaj.totalWorkingTime}) - ${puantaj.totalAmount.toLocaleString('tr-TR')} TL\n`;
          });
          detailedMessage += `\n`;
        }
      } else if (conflictingPuantajSnapshot.size > 0) {
        const totalPuantajAmount = transferredPuantajlar.reduce((sum, p) => sum + p.totalAmount, 0);
        
        detailedMessage += `📊 PUANTAJ AKTARİMİ:\n`;
        detailedMessage += `• ${conflictingPuantajSnapshot.size} adet puantaj aktarıldı\n`;
        detailedMessage += `• Toplam puantaj tutarı: ${totalPuantajAmount.toLocaleString('tr-TR')} TL\n`;
        detailedMessage += `• Kaynak: "${conflictingSantiye['Şantiye Adı']}" şantiyesi\n`;
        detailedMessage += `• Hedef: "${editSantiyeName.trim()}" şantiyesi\n`;
        detailedMessage += `• Puantajlarda "mergedFrom" etiketi ile kaynak gösterildi\n`;
        detailedMessage += `• Her puantaj cari kasa geçmişinde ayrı ayrı görünecek\n\n`;
        
        // Aktarılan puantajların listesi
        if (transferredPuantajlar.length > 0) {
          detailedMessage += `📋 AKTARILAN PUANTAJLAR:\n`;
          transferredPuantajlar.forEach((puantaj, index) => {
            detailedMessage += `  ${index + 1}. ${puantaj.puantajCount} - ${puantaj.machineName} (${puantaj.totalWorkingTime}) - ${puantaj.totalAmount.toLocaleString('tr-TR')} TL\n`;
          });
          detailedMessage += `\n`;
        }
      } else {
        detailedMessage += `📊 PUANTAJ: Aktarılacak puantaj bulunamadı\n\n`;
      }
      
      // Cari kasa bilgileri
      if (conflictingAmount > 0 || conflictingHistory.length > 0) {
        detailedMessage += `💰 CARİ KASA AKTARİMİ:\n`;
        if (conflictingAmount > 0) {
          detailedMessage += `• ${conflictingAmount.toLocaleString('tr-TR')} TL borç aktarıldı\n`;
          detailedMessage += `• Yeni toplam borç: ${newTotalAmount.toLocaleString('tr-TR')} TL\n`;
        }
        if (conflictingHistory.length > 0) {
          detailedMessage += `• ${conflictingHistory.length} adet işlem geçmişi aktarıldı\n`;
          detailedMessage += `• Cari kasa geçmişinde "şantiye birleştirme" olarak görünecek\n`;
        }
        detailedMessage += `• Kaynak: "${conflictingSantiye['Şantiye Adı']}" şantiyesi\n`;
        detailedMessage += `• Hedef: "${editSantiyeName.trim()}" şantiyesi\n\n`;
      } else {
        detailedMessage += `💰 CARİ KASA: Aktarılacak borç bulunamadı\n\n`;
      }
      
      // Kontrol etme talimatları
      detailedMessage += `🔍 KONTROL EDİN:\n`;
      detailedMessage += `• Puantajlar sayfasında "${editSantiyeName.trim()}" şantiyesinin puantajlarını kontrol edin\n`;
      detailedMessage += `• Cari Kasa sayfasında "${editSantiyeName.trim()}" şantiyesinin borç durumunu kontrol edin\n`;
      detailedMessage += `• Müşteri Listesi'nde "${editSantiyeName.trim()}" şantiyesinin güncellendiğini kontrol edin\n`;
      detailedMessage += `• Transfer edilen işlemler geçmişte "aktarıldı" etiketiyle görünecek`;

      setSuccessMessage(detailedMessage);
      setAlertOpen(true);
      setIsConflictModalOpen(false);
      onClose();
    } catch (error) {
      console.error('❌ Şantiye birleştirme hatası:', error);
      setError(`Şantiye birleştirme sırasında hata: ${error.message}`);
      setAlertOpen(true);
    }
  };

  const handleCloseModal = () => {
    // Modal kapanırken değişiklikleri geri al
    if (oldSantiyeData && selectedSantiye) {
      // Form alanlarını eski değerlere döndür
      setEditSantiyeName(oldSantiyeData['Şantiye Adı'] || '');
      setEditSantiyeCariCode(oldSantiyeData['Şantiye Cari Kodu'] || '');
      setEditSantiyePhone(selectedSantiye['Telefon'] || '');
      setEditSantiyeEmail(selectedSantiye['E-posta'] || '');
    }
    onClose();
  };

  const handleSelectCustomer = (customer) => {
    setEditSantiyeName(customer['Şantiye Adı']);
    setEditSantiyePhone(customer['Telefon'] || '');
    setEditSantiyeEmail(customer['E-posta'] || '');
    setEditSantiyeCariCode(customer['Şantiye Cari Kodu'] || '');
    setIsCustomerSelectionOpen(false);
  };

  const cancelUpdate = () => {
    setIsConfirmUpdateOpen(false);
    setUpdateChanges([]);
  };

  return (
    <>
      <Modal open={isOpen} onClose={handleCloseModal}>
        <ModalBox>
          <Typography variant="h6" gutterBottom>
            Şantiye Düzenle
          </Typography>
          
          {/* Müşteri Listesi Bölümü */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Mevcut Şantiyeler
            </Typography>
            <Box sx={{ 
              maxHeight: '300px', 
              overflow: 'auto',
              border: '1px solid #e0e0e0',
              borderRadius: '4px',
              mb: 2
            }}>
              <CustomerSelectionTable
                customers={approvedCustomers}
                enableAutoUppercase={true}
                onSelectCustomer={(santiye) => {
                  setSelectedSantiye(santiye);
                  setEditSantiyeName(santiye['Şantiye Adı'] || santiye['Müşteri Adı'] || '');
                  setEditSantiyePhone(santiye['Telefon'] || '');
                  setEditSantiyeEmail(santiye['E-posta'] || '');
                  setEditSantiyeCariCode(santiye['Şantiye Cari Kodu'] || santiye.cariCode || '');
                  setOldSantiyeData({
                    'Şantiye Adı': santiye['Şantiye Adı'] || santiye['Müşteri Adı'] || '',
                    'Şantiye Cari Kodu': santiye['Şantiye Cari Kodu'] || santiye.cariCode || ''
                  });
                  
                  // Şantiye seçildikten sonra çakışma kontrolü yap
                  setTimeout(() => {
                    // Şantiye adı çakışması kontrolü
                    const nameConflict = approvedCustomers.find(c => 
                      c.id !== santiye.id && 
                      c['Şantiye Adı']?.trim().toLowerCase() === (santiye['Şantiye Adı'] || santiye['Müşteri Adı'])?.trim().toLowerCase()
                    );
                    if (nameConflict) {
                      setNameConflict('conflict');
                      setConflictingNameSantiye(nameConflict);
                    } else {
                      setNameConflict(false);
                      setConflictingNameSantiye(null);
                    }
                    
                    // Şantiye cari kodu çakışması kontrolü
                    const codeConflict = approvedCustomers.find(c => 
                      c.id !== santiye.id && 
                      (c['Şantiye Cari Kodu'] || '').trim().toLowerCase() === (santiye['Şantiye Cari Kodu'] || santiye.cariCode || '').trim().toLowerCase()
                    );
                    if (codeConflict) {
                      setCodeConflict('conflict');
                      setConflictingCodeSantiye(codeConflict);
                    } else {
                      setCodeConflict(false);
                      setConflictingCodeSantiye(null);
                    }
                  }, 100);
                }}
                selectedId={selectedSantiye?.id}
                showOnlyShantiyeler={true}
              />
            </Box>
          </Box>
          
          <Box sx={{ mt: 2 }}>
            <TextField
              label="Şantiye Adı"
              fullWidth
              margin="normal"
              value={editSantiyeName}
              onChange={(e) => setEditSantiyeName(e.target.value)}
              error={nameConflict === 'conflict'}
              helperText={nameConflict === 'conflict' ? `Bu şantiye adı "${conflictingNameSantiye?.['Şantiye Adı'] || '-'}" tarafından kullanılıyor.` : ''}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setIsCustomerSelectionOpen(!isCustomerSelectionOpen)}
                startIcon={<ArrowDropDownIcon />}
                sx={{ minWidth: 'auto', px: 2 }}
              >
                Şantiye Seç
              </Button>
            </Box>
            {isCustomerSelectionOpen && (
              <Box sx={{ mt: 1, maxHeight: '300px', overflow: 'auto' }}>
                <CustomerSelectionTable
                  customers={approvedCustomers}
                  onSelectCustomer={handleSelectCustomer}
                  enableAutoUppercase={true}
                />
              </Box>
            )}
          </Box>
          <TextField
            label="Telefon"
            fullWidth
            margin="normal"
            value={editSantiyePhone}
            onChange={(e) => setEditSantiyePhone(e.target.value)}
          />
          <TextField
            label="E-posta"
            fullWidth
            margin="normal"
            value={editSantiyeEmail}
            onChange={(e) => setEditSantiyeEmail(e.target.value)}
          />
          <TextField
            label="Şantiye Cari Kodu"
            fullWidth
            margin="normal"
            value={editSantiyeCariCode}
            onChange={(e) => setEditSantiyeCariCode(e.target.value)}
            error={codeConflict === 'conflict'}
            helperText={codeConflict === 'conflict' ? `Bu cari kod "${conflictingCodeSantiye?.['Şantiye Cari Kodu'] || '-'}" tarafından kullanılıyor.` : ''}
          />
          <Box mt={2} display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              color="primary"
              onClick={handleUpdateSantiye}
            >
              Güncelle
            </Button>
          </Box>
        </ModalBox>
      </Modal>

      <ConflictResolutionModal
        isOpen={isConflictModalOpen}
        onClose={() => setIsConflictModalOpen(false)}
        onConfirm={handleConflictResolution}
        conflictingCustomer={conflictingSantiye}
        newCustomerData={{
          'Şantiye Adı': editSantiyeName,
          'Şantiye Cari Kodu': editSantiyeCariCode,
        }}
        oldCustomerData={oldSantiyeData}
        message={`Bu isimde veya cari kodda bir şantiye zaten var. 
          Çakışan şantiyeye ait ${conflictingSantiye?.puantajCount || 0} adet puantaj verisi bulunmaktadır. 
          Bu verileri yeni şantiyeye aktarmak ister misiniz?`}
        type="santiye"
      />

      <ConfirmUpdateModal
        isOpen={isConfirmUpdateOpen}
        onClose={cancelUpdate}
        updateChanges={updateChanges}
        confirmUpdate={proceedWithUpdate}
        cancelUpdate={cancelUpdate}
      />
    </>
  );
};

export default EditSantiyeModal;
